import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent } from "@/components/ui/card";
import { MoreHorizontal, Trash2, User } from "lucide-react";
import { format } from "date-fns";
import { DocumentAssignment } from "@/types/document";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";

interface AssignmentTableProps {
  assignments: DocumentAssignment[];
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  onDelete: (assignment: DocumentAssignment) => void;
  onPageChange: (page: number) => void;
}

export const AssignmentTable = ({
  assignments,
  totalPages,
  currentPage,
  isLoading,
  onDelete,
  onPageChange,
}: AssignmentTableProps) => {
  const getAssignmentTypeColor = (type: string) => {
    switch (type) {
      case "MANUAL":
        return "bg-info/10 text-info";
      case "AUTOMATIC":
        return "bg-success/10 text-success";
      default:
        return "bg-muted text-foreground";
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (assignments.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground h-64 flex items-center justify-center">
            No assignments found. Create your first assignment to get started.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead>Signed</TableHead>
                <TableHead className="w-16">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.map((assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell>
                    <div className="font-medium">
                      {assignment.documentTitle}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{assignment.userName}</div>
                        <div className="text-sm text-muted-foreground">
                          {assignment.userEmail}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={assignment.status} />
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={getAssignmentTypeColor(
                        assignment.assignmentType,
                      )}
                    >
                      {assignment.assignmentType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {format(new Date(assignment.assignedAt), "MMM d, yyyy")}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      by {assignment.assignedBy?.name || "Unknown"}
                    </div>
                  </TableCell>
                  <TableCell>
                    {assignment.signedAt ? (
                      <div className="text-sm text-success">
                        {format(new Date(assignment.signedAt), "MMM d, yyyy")}
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        Not signed
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => onDelete(assignment)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination (shadcn/ui style) */}
        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 0) onPageChange(currentPage - 1);
                    }}
                    aria-disabled={currentPage === 0}
                  />
                </PaginationItem>
                {/* Truncated Pagination Logic */}
                {(() => {
                  if (totalPages <= 7) {
                    // Show all pages
                    return Array.from({ length: totalPages }).map((_, idx) => (
                      <PaginationItem key={idx}>
                        <PaginationLink
                          href="#"
                          isActive={currentPage === idx}
                          onClick={(e) => {
                            e.preventDefault();
                            onPageChange(idx);
                          }}
                        >
                          {idx + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ));
                  }
                  // Truncated windowed pagination
                  const pageLinks = [];
                  const window = 2;
                  const start = Math.max(2, currentPage + 1 - window);
                  const end = Math.min(
                    totalPages - 1,
                    currentPage + 1 + window,
                  );
                  // Always show first page
                  pageLinks.push(
                    <PaginationItem key={1}>
                      <PaginationLink
                        href="#"
                        isActive={currentPage === 0}
                        onClick={(e) => {
                          e.preventDefault();
                          onPageChange(0);
                        }}
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>,
                  );
                  // Ellipsis after first page
                  if (start > 2) {
                    pageLinks.push(
                      <PaginationItem key="start-ellipsis">
                        <span className="px-2">...</span>
                      </PaginationItem>,
                    );
                  }
                  // Pages in window
                  for (let i = start; i <= end; i++) {
                    pageLinks.push(
                      <PaginationItem key={i}>
                        <PaginationLink
                          href="#"
                          isActive={currentPage === i - 1}
                          onClick={(e) => {
                            e.preventDefault();
                            onPageChange(i - 1);
                          }}
                        >
                          {i}
                        </PaginationLink>
                      </PaginationItem>,
                    );
                  }
                  // Ellipsis before last page
                  if (end < totalPages - 1) {
                    pageLinks.push(
                      <PaginationItem key="end-ellipsis">
                        <span className="px-2">...</span>
                      </PaginationItem>,
                    );
                  }
                  // Always show last page
                  pageLinks.push(
                    <PaginationItem key={totalPages}>
                      <PaginationLink
                        href="#"
                        isActive={currentPage === totalPages - 1}
                        onClick={(e) => {
                          e.preventDefault();
                          onPageChange(totalPages - 1);
                        }}
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>,
                  );
                  return pageLinks;
                })()}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages - 1)
                        onPageChange(currentPage + 1);
                    }}
                    aria-disabled={currentPage === totalPages - 1}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
